import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient, withCsrfHeader } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2,
  Mail,
  CheckCircle2,
  AlertTriangle,
  Link2,
  Link2Off,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Users,
  FileText,
  UserCheck,
  MapPin,
  ArrowDownToLine,
  Download,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

type GmailAccount = {
  userId: string;
  name: string | null;
  email: string | null;
  gmailAddress: string;
  hasReadScope: boolean;
};

type Region = {
  id: string;
  name: string;
};

type MatchedRecord = { id: string; name: string; type: "lead" | "client" };

type Booking = {
  emailId: string;
  subject: string;
  emailDate: string;
  isDuplicate: boolean;
  matchedRecord: MatchedRecord | null;
  invoiceToken: string | null;
  rawInvoiceNumber: string | null;
  invoiceSurvived: boolean;
  invoiceUrlFound: boolean;
  type: string;
  accountName: string;
  contactName: string | null;
  contactEmail: string | null;
  phone: string | null;
  address: string | null;
  zipCode: string | null;
  dateOfEvent: string | null;
  eventTime: string | null;
  eventRate: number | null;
  discount: number | null;
  city: string | null;
  state: string | null;
  eventDetails: string | null;
  relatedEmails: { emailId: string; subject: string; emailDate: string }[];
  // UI state
  selected: boolean;
  created: boolean;
  skipped: boolean;
  creating: boolean;
  ingesting: boolean;
  emailsIngested: boolean;
  error: string | null;
  createdClientId: string | null;
  invoiceNumber: string | null;
  tokenRestored: boolean;
  regionId: string | null;
  clientTypeId: string | null;
};

type Lead = {
  emailId: string;
  subject: string;
  emailDate: string;
  isDuplicate: boolean;
  matchedRecord: MatchedRecord | null;
  type: string;
  accountName: string;
  contactName: string | null;
  contactEmail: string | null;
  phone: string | null;
  address: string | null;
  zipCode: string | null;
  website: string | null;
  city: string | null;
  state: string | null;
  relatedEmails: { emailId: string; subject: string; emailDate: string }[];
  // UI state
  selected: boolean;
  created: boolean;
  skipped: boolean;
  creating: boolean;
  ingesting: boolean;
  emailsIngested: boolean;
  error: string | null;
  createdLeadId: string | null;
  regionId: string | null;
  emailsExpanded: boolean;
};

type ScanResult = {
  bookings: Omit<Booking, "selected" | "created" | "skipped" | "creating" | "ingesting" | "emailsIngested" | "error" | "createdClientId" | "invoiceNumber" | "tokenRestored" | "rawInvoiceNumber" | "regionId" | "clientTypeId">[];
  leads: Omit<Lead, "selected" | "created" | "skipped" | "creating" | "ingesting" | "emailsIngested" | "error" | "createdLeadId" | "regionId" | "emailsExpanded">[];
  scanned: number;
};

type CreateResult = {
  ok: boolean;
  clientId: string;
  invoiceId: string;
  invoiceNumber: string;
  tokenRestored: boolean;
  invoiceSurvived: boolean;
  accountName: string;
};

type Step = "setup" | "scan" | "review" | "done";

export default function EmailRecoveryWizard() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("setup");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [startDate, setStartDate] = useState("2026-05-16");
  const [endDate, setEndDate] = useState("2026-06-10");
  const [scanning, setScanning] = useState(false);
  const [scannedCount, setScannedCount] = useState(0);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanMessage, setScanMessage] = useState("Starting…");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [bulkRegionId, setBulkRegionId] = useState<string>("");
  const [creatingAll, setCreatingAll] = useState(false);
  const [creatingAllLeads, setCreatingAllLeads] = useState(false);
  const [filter, setFilter] = useState<"all" | "new" | "dup">("all");

  const { data: accounts = [], isLoading: accountsLoading } = useQuery<GmailAccount[]>({
    queryKey: ["/api/email-recovery/accounts"],
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/email-recovery/regions"],
  });

  const { data: clientTypes = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/client-types"],
  });

  const updateBooking = useCallback(
    (idx: number, field: keyof Booking, value: any) => {
      setBookings((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: value };
        return next;
      });
    },
    []
  );

  const updateLead = useCallback(
    (idx: number, field: keyof Lead, value: any) => {
      setLeads((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: value };
        return next;
      });
    },
    []
  );

  // ── Helpers ─────────────────────────────────────────────────────────────

  /** Fuzzy-match a lead's city/state against the workspace region list. */
  function suggestRegionForLead(
    city: string | null,
    state: string | null,
    address: string | null,
    regionList: Region[]
  ): string | null {
    if (!regionList.length) return null;
    let c = (city || "").toLowerCase().trim();
    let s = (state || "").toLowerCase().trim();
    // Fallback: extract city from full address "Street, City, State, Zip"
    if (!c && !s && address) {
      const parts = address.split(",").map((p) => p.trim()).filter(Boolean);
      if (parts.length >= 3) {
        const hasZip = /^\d/.test(parts[parts.length - 1]);
        s = (parts[parts.length - (hasZip ? 2 : 1)] || "").toLowerCase();
        c = (parts[parts.length - (hasZip ? 3 : 2)] || "").toLowerCase();
      }
    }
    for (const r of regionList) {
      const n = r.name.toLowerCase().trim();
      if (c && s && n.includes(c) && n.includes(s)) return r.id;
      if (c && (n === c || n.startsWith(c + ",") || n.startsWith(c + " "))) return r.id;
    }
    return null;
  }

  function exportBookingsCSV() {
    const headers = [
      "Account Name", "Contact Name", "Contact Email", "Event Date",
      "Event Time", "Rate", "Address", "City", "State", "Zip",
      "Invoice Token", "Is Duplicate", "Status",
    ];
    const rows = bookings.map((b) => [
      b.accountName, b.contactName || "", b.contactEmail || "",
      b.dateOfEvent || "", b.eventTime || "", b.eventRate?.toString() || "",
      b.address || "", b.city || "", b.state || "", b.zipCode || "",
      b.invoiceToken || "",
      b.isDuplicate ? "Duplicate" : "New",
      b.created ? "Created" : b.skipped ? "Skipped" : "Pending",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "recovered_bookings.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportLeadsCSV() {
    const headers = [
      "Account Name", "Contact Name", "Contact Email", "Phone",
      "Address", "City", "State", "Zip", "Website",
      "First Email Date", "Email Count", "Is Duplicate",
    ];
    const rows = leads.map((l) => [
      l.accountName, l.contactName || "", l.contactEmail || "",
      l.phone || "", l.address || "", l.city || "", l.state || "",
      l.zipCode || "", l.website || "", l.emailDate || "",
      l.relatedEmails.length.toString(),
      l.isDuplicate ? "Duplicate" : "New",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "recovered_leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  const handleBulkAssignRegion = () => {
    if (!bulkRegionId) return;
    const affected = leads.filter((l) => l.selected && !l.created && !l.skipped).length;
    setLeads((prev) =>
      prev.map((l) =>
        l.selected && !l.created && !l.skipped ? { ...l, regionId: bulkRegionId } : l
      )
    );
    setBulkRegionId("");
    toast({ title: "Region assigned", description: `Assigned to ${affected} lead${affected !== 1 ? "s" : ""}.` });
  };

  const handleScan = async () => {
    if (!selectedUserId) {
      toast({ title: "Select a Gmail account first", variant: "destructive" });
      return;
    }
    setScanning(true);
    setScanProgress(0);
    setScanMessage("Connecting to Gmail…");
    setStep("scan");

    try {
      // Start the scan as a background job. Server validates immediately and
      // returns a jobId — the scan runs server-side regardless of connection state.
      const startResp = await fetch("/api/email-recovery/scan", {
        method: "POST",
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        body: JSON.stringify({ userId: selectedUserId, startDate, endDate }),
        credentials: "include",
      });

      if (!startResp.ok) {
        const err = await startResp.json().catch(() => ({}));
        throw new Error((err as any).message || "Scan failed");
      }

      const { jobId } = await startResp.json();

      // Poll every 2 seconds for progress and completion.
      let finalResult: ScanResult | null = null;
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));

        const pollResp = await fetch(`/api/email-recovery/scan/${jobId}`, {
          credentials: "include",
        });
        if (!pollResp.ok) throw new Error("Failed to check scan status");

        const state = await pollResp.json();
        if (typeof state.progress === "number") setScanProgress(state.progress);
        if (state.message) setScanMessage(state.message);

        if (state.status === "error") throw new Error(state.error || "Scan failed");
        if (state.status === "done") {
          finalResult = state.result as ScanResult;
          break;
        }
      }

      if (!finalResult) throw new Error("Scan ended without returning results");

      setScanProgress(100);
      setScanMessage("Complete!");
      setScannedCount(finalResult.scanned);

      const withUiState: Booking[] = (finalResult.bookings || []).map((b: any) => ({
        ...b,
        relatedEmails: b.relatedEmails ?? [],
        rawInvoiceNumber: b.invoiceNumber ?? null,
        matchedRecord: b.matchedRecord ?? null,
        selected: !b.isDuplicate,
        created: false,
        skipped: false,
        creating: false,
        ingesting: false,
        emailsIngested: false,
        error: null,
        createdClientId: null,
        invoiceNumber: null,
        tokenRestored: false,
        regionId: null,
        clientTypeId: null,
      }));
      // Sort leads chronologically by their earliest related email date so the
      // user sees outreach in the order it actually happened.
      const earliestDate = (l: any): number => {
        const dates = (l.relatedEmails || []).map((e: any) => new Date(e.emailDate || 0).getTime());
        if (dates.length > 0) return Math.min(...dates);
        return new Date(l.emailDate || 0).getTime();
      };

      const leadsWithUiState: Lead[] = (finalResult.leads || [])
        .slice()
        .sort((a: any, b: any) => earliestDate(a) - earliestDate(b))
        .map((l: any) => ({
          ...l,
          relatedEmails: l.relatedEmails ?? [],
          matchedRecord: l.matchedRecord ?? null,
          selected: !l.isDuplicate,
          created: false,
          skipped: false,
          creating: false,
          ingesting: false,
          emailsIngested: false,
          error: null,
          createdLeadId: null,
          regionId: suggestRegionForLead(l.city, l.state, l.address, regions),
          emailsExpanded: false,
        }));
      setBookings(withUiState);
      setLeads(leadsWithUiState);

      // Brief pause so user sees 100% before navigating.
      await new Promise((r) => setTimeout(r, 600));
      setStep("review");
    } catch (err: any) {
      toast({
        title: "Scan failed",
        description: err?.message || "Check console for details",
        variant: "destructive",
      });
      setStep("setup");
    } finally {
      setScanning(false);
    }
  };

  const createOne = async (idx: number): Promise<void> => {
    const b = bookings[idx];
    if (b.created || b.skipped || b.creating) return;

    updateBooking(idx, "creating", true);
    updateBooking(idx, "error", null);

    try {
      const resp = await apiRequest("POST", "/api/email-recovery/create", {
        accountName: b.accountName,
        contactName: b.contactName,
        contactEmail: b.contactEmail,
        phone: b.phone,
        address: b.address,
        zipCode: b.zipCode,
        dateOfEvent: b.dateOfEvent,
        eventTime: b.eventTime,
        eventRate: b.eventRate,
        discount: b.discount,
        city: b.city,
        state: b.state,
        eventDetails: b.eventDetails,
        invoiceToken: b.invoiceToken,
        invoiceNumber: b.rawInvoiceNumber,
        repName: "Jennifer Obert",
        relatedEmails: b.relatedEmails,
        regionId: b.regionId,
        clientTypeId: b.clientTypeId,
      });

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.message || "Create failed");
      }

      const result: CreateResult = await resp.json();
      setBookings((prev) => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          creating: false,
          created: true,
          createdClientId: result.clientId,
          invoiceNumber: result.invoiceNumber,
          tokenRestored: result.tokenRestored,
        };
        return next;
      });
    } catch (err: any) {
      setBookings((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], creating: false, error: err.message };
        return next;
      });
    }
  };

  const createOneLead = async (idx: number): Promise<void> => {
    const l = leads[idx];
    if (l.created || l.skipped || l.creating) return;
    if (!l.regionId) {
      toast({ title: "Assign a region first", description: "Use the region dropdown on the lead card.", variant: "destructive" });
      return;
    }

    updateLead(idx, "creating", true);
    updateLead(idx, "error", null);

    try {
      const resp = await apiRequest("POST", "/api/email-recovery/create-lead", {
        accountName: l.accountName,
        contactName: l.contactName,
        contactEmail: l.contactEmail,
        phone: l.phone,
        address: l.address,
        zipCode: l.zipCode,
        website: l.website,
        city: l.city,
        state: l.state,
        subject: l.subject,
        regionId: l.regionId,
        repName: "Jennifer Obert",
        relatedEmails: l.relatedEmails,
      });

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.message || "Create failed");
      }

      const result = await resp.json();
      setLeads((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], creating: false, created: true, createdLeadId: result.leadId };
        return next;
      });
    } catch (err: any) {
      setLeads((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], creating: false, error: err.message };
        return next;
      });
    }
  };

  const handleCreateAll = async () => {
    const toCreate = bookings
      .map((b, i) => ({ b, i }))
      .filter(({ b }) => b.selected && !b.created && !b.skipped && !!b.regionId);
    const missingRegion = bookings.filter((b) => b.selected && !b.created && !b.skipped && !b.regionId).length;
    if (missingRegion > 0) {
      toast({ title: `${missingRegion} booking${missingRegion !== 1 ? "s" : ""} missing region`, description: "Assign a region before creating.", variant: "destructive" });
    }

    if (toCreate.length === 0) {
      toast({ title: "Nothing to create", description: "Select some bookings first." });
      return;
    }

    setCreatingAll(true);
    for (const { i } of toCreate) {
      await createOne(i);
    }
    setCreatingAll(false);
    queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
    queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
  };

  const handleCreateAllLeads = async () => {
    const toCreate = leads
      .map((l, i) => ({ l, i }))
      .filter(({ l }) => l.selected && !l.created && !l.skipped && !!l.regionId);
    const missing = leads.filter((l) => l.selected && !l.created && !l.skipped && !l.regionId).length;
    if (missing > 0) {
      toast({ title: `${missing} lead${missing !== 1 ? "s" : ""} missing region`, description: "Assign a region before creating.", variant: "destructive" });
    }

    if (toCreate.length === 0) {
      toast({ title: "Nothing to create", description: "Select some leads first." });
      return;
    }

    setCreatingAllLeads(true);
    for (const { i } of toCreate) {
      await createOneLead(i);
    }
    setCreatingAllLeads(false);
    queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
  };

  const skipOne = (idx: number) => {
    updateBooking(idx, "skipped", true);
    updateBooking(idx, "selected", false);
  };

  const skipOneLead = (idx: number) => {
    updateLead(idx, "skipped", true);
    updateLead(idx, "selected", false);
  };

  const ingestEmailsForBooking = async (idx: number) => {
    const b = bookings[idx];
    if (!b.matchedRecord || b.ingesting || b.emailsIngested) return;
    updateBooking(idx, "ingesting", true);
    try {
      const resp = await apiRequest("POST", "/api/email-recovery/ingest-emails", {
        existingId: b.matchedRecord.id,
        existingType: b.matchedRecord.type,
        relatedEmails: b.relatedEmails,
        repName: null,
      });
      const data = await resp.json();
      if (data.ok) {
        updateBooking(idx, "emailsIngested", true);
        updateBooking(idx, "skipped", true);
        updateBooking(idx, "selected", false);
        toast({
          title: "Email history imported",
          description: `${data.count} email${data.count !== 1 ? "s" : ""} added to ${b.matchedRecord.name}'s timeline.`,
        });
      }
    } catch (err: any) {
      toast({ title: "Import failed", description: err?.message, variant: "destructive" });
    } finally {
      updateBooking(idx, "ingesting", false);
    }
  };

  const ingestEmailsForLead = async (idx: number) => {
    const l = leads[idx];
    if (!l.matchedRecord || l.ingesting || l.emailsIngested) return;
    updateLead(idx, "ingesting", true);
    try {
      const resp = await apiRequest("POST", "/api/email-recovery/ingest-emails", {
        existingId: l.matchedRecord.id,
        existingType: l.matchedRecord.type,
        relatedEmails: l.relatedEmails,
        repName: null,
      });
      const data = await resp.json();
      if (data.ok) {
        updateLead(idx, "emailsIngested", true);
        updateLead(idx, "skipped", true);
        updateLead(idx, "selected", false);
        toast({
          title: "Email history imported",
          description: `${data.count} email${data.count !== 1 ? "s" : ""} added to ${l.matchedRecord.name}'s timeline.`,
        });
      }
    } catch (err: any) {
      toast({ title: "Import failed", description: err?.message, variant: "destructive" });
    } finally {
      updateLead(idx, "ingesting", false);
    }
  };

  const visibleBookings = bookings.filter((b) => {
    if (filter === "new") return !b.isDuplicate;
    if (filter === "dup") return b.isDuplicate;
    return true;
  });

  const selectedBookingCount = bookings.filter((b) => b.selected && !b.created && !b.skipped).length;
  const createdBookingCount = bookings.filter((b) => b.created).length;
  const restoredCount = bookings.filter((b) => b.created && b.tokenRestored).length;
  const newLinkCount = bookings.filter((b) => b.created && !b.tokenRestored).length;
  const selectedLeadCount = leads.filter((l) => l.selected && !l.created && !l.skipped).length;
  const createdLeadCount = leads.filter((l) => l.created).length;

  // ── Step: SETUP ─────────────────────────────────────────────────────────
  if (step === "setup") {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6 text-blue-600" />
            Email Recovery Wizard
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Scan Gmail to reconstruct lost bookings <em>and</em> leads from the May 16–Jun 10 window. Booking confirmations get their original invoice payment link restored. Lead-stage contacts get re-added to the leads pipeline.
          </p>
        </div>

        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="space-y-2">
              <Label>Gmail account to scan</Label>
              {accountsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading accounts…
                </div>
              ) : accounts.length === 0 ? (
                <p className="text-sm text-red-600">
                  No Gmail accounts connected. Have the sales rep connect Gmail under Settings → Integrations.
                </p>
              ) : (
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger data-testid="select-gmail-account">
                    <SelectValue placeholder="Select account…" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.userId} value={a.userId}>
                        <div className="flex flex-col">
                          <span>{a.name}</span>
                          <span className="text-xs text-muted-foreground">{a.gmailAddress || a.email}</span>
                        </div>
                        {!a.hasReadScope && (
                          <Badge variant="destructive" className="ml-2 text-xs">No read scope</Badge>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>To date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="input-end-date"
                />
              </div>
            </div>

            <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-300 space-y-1">
              <p><strong>What this scans for:</strong></p>
              <ul className="list-disc list-inside space-y-0.5 ml-1">
                <li><strong>Bookings</strong> — confirmed event emails with dates agreed. Restores the original invoice payment link token so old links keep working.</li>
                <li><strong>Leads</strong> — outreach, follow-up, and inquiry emails to venues that hadn't confirmed yet. Re-adds them to the leads pipeline as "contacted."</li>
              </ul>
            </div>

            <Button
              className="w-full"
              onClick={handleScan}
              disabled={!selectedUserId || accountsLoading}
              data-testid="button-scan-emails"
            >
              <Mail className="h-4 w-4 mr-2" />
              Scan Emails
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Step: SCANNING ──────────────────────────────────────────────────────
  if (step === "scan") {
    return (
      <div className="max-w-2xl mx-auto p-6 flex flex-col items-center justify-center min-h-[400px] gap-6">
        <div className="w-full max-w-md space-y-5">
          <div className="text-center space-y-1">
            <h2 className="text-xl font-semibold">Scanning emails…</h2>
            <p className="text-sm text-muted-foreground min-h-[1.25rem]">{scanMessage}</p>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-secondary rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${scanProgress}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{scanProgress}% complete</span>
            <span className="flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Don't close this tab
            </span>
          </div>

          {/* Phase labels */}
          <div className="grid grid-cols-5 gap-1 text-[10px] text-center text-muted-foreground">
            {[
              { label: "Fetch IDs", pct: 8 },
              { label: "Filter", pct: 25 },
              { label: "Load bodies", pct: 55 },
              { label: "Regex extract", pct: 62 },
              { label: "AI classify", pct: 100 },
            ].map(({ label, pct }) => (
              <div
                key={label}
                className={`py-1 px-0.5 rounded ${scanProgress >= pct ? "text-blue-600 font-medium" : ""}`}
              >
                {scanProgress >= pct ? "✓ " : ""}{label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Step: REVIEW ────────────────────────────────────────────────────────
  if (step === "review") {
    const newBookingCount = bookings.filter((b) => !b.isDuplicate).length;
    const dupBookingCount = bookings.filter((b) => b.isDuplicate).length;
    const tokenFoundCount = bookings.filter((b) => b.invoiceToken).length;
    const newLeadsCount = leads.filter((l) => !l.isDuplicate).length;

    return (
      <div className="max-w-5xl mx-auto p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Review Recovered Records
            </h1>
            <p className="text-sm text-muted-foreground">
              Scanned {scannedCount} emails &nbsp;·&nbsp;
              {bookings.length} booking{bookings.length !== 1 ? "s" : ""} &nbsp;·&nbsp;
              {leads.length} lead{leads.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {bookings.length > 0 && (
              <Button variant="outline" size="sm" onClick={exportBookingsCSV} data-testid="button-export-bookings-csv">
                <Download className="h-3.5 w-3.5 mr-1" /> Bookings CSV
              </Button>
            )}
            {leads.length > 0 && (
              <Button variant="outline" size="sm" onClick={exportLeadsCSV} data-testid="button-export-leads-csv">
                <Download className="h-3.5 w-3.5 mr-1" /> Leads CSV
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setStep("setup")}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          </div>
        </div>

        {/* ── BOOKINGS SECTION ── */}
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />
                Bookings ({bookings.length})
              </h2>
              <p className="text-xs text-muted-foreground">
                {newBookingCount} new · {dupBookingCount} duplicates · {tokenFoundCount} links to restore
              </p>
            </div>
            <Button
              size="sm"
              onClick={handleCreateAll}
              disabled={creatingAll || selectedBookingCount === 0}
              data-testid="button-create-all-bookings"
            >
              {creatingAll ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</>
              ) : (
                <>Create {selectedBookingCount} Booking{selectedBookingCount !== 1 ? "s" : ""}</>
              )}
            </Button>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 text-sm">
            {(["all", "new", "dup"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-full border transition-colors ${
                  filter === f
                    ? "bg-blue-600 text-white border-blue-600"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {f === "all" ? `All (${bookings.length})` : f === "new" ? `New (${newBookingCount})` : `Duplicates (${dupBookingCount})`}
              </button>
            ))}
          </div>

          {/* Booking cards */}
          <div className="space-y-3">
            {visibleBookings.length === 0 && (
              <div className="text-center text-muted-foreground py-8 text-sm">
                No bookings in this filter.
              </div>
            )}
            {visibleBookings.map((b, visIdx) => {
              const globalIdx = bookings.findIndex((x) => x.emailId === b.emailId);
              return (
                <Card
                  key={b.emailId}
                  className={`transition-opacity ${b.created ? "opacity-60" : b.skipped ? "opacity-40" : ""}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <div className="pt-1 shrink-0">
                        <Checkbox
                          checked={b.selected}
                          onCheckedChange={(v) => updateBooking(globalIdx, "selected", !!v)}
                          disabled={b.created || b.skipped}
                          data-testid={`checkbox-booking-${visIdx}`}
                        />
                      </div>

                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Venue / Organization</Label>
                          <Input
                            value={b.accountName}
                            onChange={(e) => updateBooking(globalIdx, "accountName", e.target.value)}
                            disabled={b.created || b.skipped}
                            className="h-8 text-sm font-medium"
                            data-testid={`input-account-name-${visIdx}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Contact Name</Label>
                          <Input
                            value={b.contactName || ""}
                            onChange={(e) => updateBooking(globalIdx, "contactName", e.target.value || null)}
                            disabled={b.created || b.skipped}
                            className="h-8 text-sm"
                            placeholder="—"
                            data-testid={`input-contact-name-${visIdx}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Contact Email</Label>
                          <Input
                            value={b.contactEmail || ""}
                            onChange={(e) => updateBooking(globalIdx, "contactEmail", e.target.value || null)}
                            disabled={b.created || b.skipped}
                            className="h-8 text-sm"
                            placeholder="—"
                            data-testid={`input-contact-email-${visIdx}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Event Date</Label>
                          <Input
                            type="date"
                            value={b.dateOfEvent || ""}
                            onChange={(e) => updateBooking(globalIdx, "dateOfEvent", e.target.value || null)}
                            disabled={b.created || b.skipped}
                            className="h-8 text-sm"
                            data-testid={`input-event-date-${visIdx}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Event Time</Label>
                          <Input
                            type="text"
                            value={b.eventTime || ""}
                            onChange={(e) => updateBooking(globalIdx, "eventTime", e.target.value || null)}
                            disabled={b.created || b.skipped}
                            className="h-8 text-sm"
                            placeholder="e.g. 2:00 PM"
                            data-testid={`input-event-time-${visIdx}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Rate ($)</Label>
                          <Input
                            type="number"
                            value={b.eventRate ?? ""}
                            onChange={(e) =>
                              updateBooking(globalIdx, "eventRate", e.target.value ? Number(e.target.value) : null)
                            }
                            disabled={b.created || b.skipped}
                            className="h-8 text-sm"
                            placeholder="—"
                            data-testid={`input-event-rate-${visIdx}`}
                          />
                        </div>
                        <div className="space-y-1 col-span-1 md:col-span-2">
                          <Label className="text-xs text-muted-foreground">Full Address</Label>
                          <Input
                            value={b.address || ""}
                            onChange={(e) => updateBooking(globalIdx, "address", e.target.value || null)}
                            disabled={b.created || b.skipped}
                            className="h-8 text-sm"
                            placeholder="Street, City, State, Zip"
                            data-testid={`input-address-${visIdx}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Region</Label>
                          <Select
                            value={b.regionId || ""}
                            onValueChange={(v) => updateBooking(globalIdx, "regionId", v || null)}
                            disabled={b.created || b.skipped}
                          >
                            <SelectTrigger className="h-8 text-sm" data-testid={`select-region-booking-${visIdx}`}>
                              <SelectValue placeholder="Select region" />
                            </SelectTrigger>
                            <SelectContent>
                              {regions.map((r: any) => (
                                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Client Type</Label>
                          <Select
                            value={b.clientTypeId || ""}
                            onValueChange={(v) => updateBooking(globalIdx, "clientTypeId", v || null)}
                            disabled={b.created || b.skipped}
                          >
                            <SelectTrigger className="h-8 text-sm" data-testid={`select-client-type-booking-${visIdx}`}>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {clientTypes.map((ct: any) => (
                                <SelectItem key={ct.id} value={ct.id}>{ct.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Right: badges + actions */}
                      <div className="shrink-0 flex flex-col items-end gap-2 min-w-[130px]">
                        <div className="flex flex-col gap-1 items-end">
                          {b.isDuplicate && (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" /> Duplicate
                            </Badge>
                          )}
                          {b.isDuplicate && b.matchedRecord && (
                            <span className="text-[10px] text-muted-foreground text-right leading-tight max-w-[130px]">
                              Matched:{" "}
                              <span className="font-medium text-foreground">{b.matchedRecord.name}</span>
                              {" "}({b.matchedRecord.type})
                            </span>
                          )}
                          {b.invoiceToken && !b.invoiceSurvived && (
                            <Badge className="text-xs bg-emerald-600 text-white">
                              <Link2 className="h-3 w-3 mr-1" /> Link found
                            </Badge>
                          )}
                          {b.invoiceSurvived && (
                            <Badge className="text-xs bg-blue-600 text-white">
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Link alive
                            </Badge>
                          )}
                          {!b.invoiceToken && !b.invoiceSurvived && (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">
                              <Link2Off className="h-3 w-3 mr-1" /> New link
                            </Badge>
                          )}
                        </div>

                        <p className="text-xs text-muted-foreground text-right max-w-[130px] truncate" title={b.subject}>
                          {b.subject}
                        </p>

                        {!b.created && !b.skipped && (
                          <div className="flex flex-col gap-1 mt-auto items-end">
                            {b.isDuplicate && b.matchedRecord && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs px-2 border-blue-400 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 w-full"
                                onClick={() => ingestEmailsForBooking(globalIdx)}
                                disabled={b.ingesting || b.emailsIngested}
                                data-testid={`button-import-history-booking-${visIdx}`}
                              >
                                {b.ingesting
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <><ArrowDownToLine className="h-3 w-3 mr-1" />Import history</>
                                }
                              </Button>
                            )}
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs px-2"
                                onClick={() => skipOne(globalIdx)}
                                disabled={b.creating || creatingAll}
                                data-testid={`button-skip-booking-${visIdx}`}
                              >
                                Skip
                              </Button>
                              <Button
                                size="sm"
                                className="h-7 text-xs px-2"
                                onClick={() => createOne(globalIdx)}
                                disabled={b.creating || creatingAll || !b.regionId}
                                data-testid={`button-create-booking-${visIdx}`}
                              >
                                {b.creating ? <Loader2 className="h-3 w-3 animate-spin" /> : "Create"}
                              </Button>
                            </div>
                          </div>
                        )}

                        {b.created && (
                          <div className="flex flex-col items-end gap-0.5 mt-auto">
                            <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" /> Created
                            </span>
                            {b.invoiceNumber && (
                              <span className="text-xs text-muted-foreground">#{b.invoiceNumber}</span>
                            )}
                            {b.tokenRestored && (
                              <span className="text-xs text-emerald-600">Link restored ✓</span>
                            )}
                          </div>
                        )}

                        {b.skipped && (
                          <span className="text-xs text-muted-foreground mt-auto">Skipped</span>
                        )}

                        {b.error && (
                          <span className="text-xs text-red-500 mt-auto text-right">{b.error}</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* ── LEADS SECTION ── */}
        {leads.length > 0 && (
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <h2 className="font-semibold text-base flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-indigo-600" />
                  Leads ({leads.length})
                </h2>
                <p className="text-xs text-muted-foreground">
                  {newLeadsCount} new · sorted chronologically · each needs a region before import
                </p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleCreateAllLeads}
                disabled={creatingAllLeads || selectedLeadCount === 0}
                data-testid="button-create-all-leads"
              >
                {creatingAllLeads ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</>
                ) : (
                  <>Add {selectedLeadCount} Lead{selectedLeadCount !== 1 ? "s" : ""}</>
                )}
              </Button>
            </div>

            {/* Bulk region assignment — shown when 2+ leads are checked */}
            {selectedLeadCount >= 2 && (
              <div className="flex items-center gap-2 p-2.5 rounded-md bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800">
                <MapPin className="h-4 w-4 text-indigo-600 shrink-0" />
                <span className="text-sm text-indigo-700 dark:text-indigo-300 shrink-0">
                  {selectedLeadCount} leads selected — bulk assign region:
                </span>
                <Select value={bulkRegionId} onValueChange={setBulkRegionId}>
                  <SelectTrigger className="h-7 text-xs flex-1 min-w-0 max-w-xs" data-testid="select-bulk-region">
                    <SelectValue placeholder="Pick region…" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="h-7 text-xs shrink-0"
                  disabled={!bulkRegionId}
                  onClick={handleBulkAssignRegion}
                  data-testid="button-bulk-assign-region"
                >
                  Apply
                </Button>
              </div>
            )}

            {/* Lead cards */}
            <div className="space-y-2">
              {leads.map((l, idx) => (
                <Card
                  key={l.emailId}
                  className={`transition-opacity ${l.created ? "opacity-60" : l.skipped ? "opacity-40" : ""}`}
                >
                  <CardContent className="p-4 space-y-2">
                    <div className="flex gap-3">
                      <div className="pt-1 shrink-0">
                        <Checkbox
                          checked={l.selected}
                          onCheckedChange={(v) => updateLead(idx, "selected", !!v)}
                          disabled={l.created || l.skipped}
                          data-testid={`checkbox-lead-${idx}`}
                        />
                      </div>

                      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {/* Row 1: name + contact email */}
                        <div className="space-y-1 col-span-2 md:col-span-2">
                          <Label className="text-xs text-muted-foreground">Venue / Organization</Label>
                          <Input
                            value={l.accountName}
                            onChange={(e) => updateLead(idx, "accountName", e.target.value)}
                            disabled={l.created || l.skipped}
                            className="h-8 text-sm font-medium"
                            data-testid={`input-lead-name-${idx}`}
                          />
                        </div>
                        <div className="space-y-1 col-span-2 md:col-span-2">
                          <Label className="text-xs text-muted-foreground">Contact Email</Label>
                          <Input
                            value={l.contactEmail || ""}
                            onChange={(e) => updateLead(idx, "contactEmail", e.target.value || null)}
                            disabled={l.created || l.skipped}
                            className="h-8 text-sm"
                            placeholder="—"
                            data-testid={`input-lead-email-${idx}`}
                          />
                        </div>
                        {/* Row 2: address hint + region */}
                        <div className="space-y-1 col-span-2 md:col-span-2">
                          <Label className="text-xs text-muted-foreground">
                            {l.city || l.state
                              ? `Location · ${[l.city, l.state].filter(Boolean).join(", ")}`
                              : "Address"}
                          </Label>
                          <Input
                            value={l.address || ""}
                            onChange={(e) => updateLead(idx, "address", e.target.value || null)}
                            disabled={l.created || l.skipped}
                            className="h-8 text-sm"
                            placeholder="—"
                            data-testid={`input-lead-address-${idx}`}
                          />
                        </div>
                        <div className="space-y-1 col-span-2 md:col-span-2">
                          <Label className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> Region
                            {!l.regionId && !l.created && !l.skipped && (
                              <span className="text-amber-500 ml-1">· required</span>
                            )}
                          </Label>
                          {regions.length === 0 ? (
                            <p className="text-xs text-red-600">No regions found</p>
                          ) : (
                            <Select
                              value={l.regionId || ""}
                              onValueChange={(v) => updateLead(idx, "regionId", v || null)}
                              disabled={l.created || l.skipped}
                            >
                              <SelectTrigger className="h-8 text-sm" data-testid={`select-lead-region-${idx}`}>
                                <SelectValue placeholder="Assign region…" />
                              </SelectTrigger>
                              <SelectContent>
                                {regions.map((r) => (
                                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>

                      {/* Right: badges + actions */}
                      <div className="shrink-0 flex flex-col items-end gap-2 min-w-[130px]">
                        <div className="flex flex-col gap-1 items-end">
                          {l.isDuplicate && (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" /> Duplicate
                            </Badge>
                          )}
                          {l.isDuplicate && l.matchedRecord && (
                            <span className="text-[10px] text-muted-foreground text-right leading-tight max-w-[130px]">
                              Matched:{" "}
                              <span className="font-medium text-foreground">{l.matchedRecord.name}</span>
                              {" "}({l.matchedRecord.type})
                            </span>
                          )}
                        </div>

                        <p className="text-[10px] text-muted-foreground text-right max-w-[130px]">
                          {l.relatedEmails.length} email{l.relatedEmails.length !== 1 ? "s" : ""} ·{" "}
                          {l.emailDate ? new Date(l.emailDate).toLocaleDateString() : "—"}
                        </p>

                        {!l.created && !l.skipped && (
                          <div className="flex flex-col gap-1 mt-auto items-end">
                            {l.isDuplicate && l.matchedRecord && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs px-2 border-blue-400 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 w-full"
                                onClick={() => ingestEmailsForLead(idx)}
                                disabled={l.ingesting || l.emailsIngested}
                                data-testid={`button-import-history-lead-${idx}`}
                              >
                                {l.ingesting
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <><ArrowDownToLine className="h-3 w-3 mr-1" />Import history</>
                                }
                              </Button>
                            )}
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs px-2"
                                onClick={() => skipOneLead(idx)}
                                disabled={l.creating || creatingAllLeads}
                                data-testid={`button-skip-lead-${idx}`}
                              >
                                Skip
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                className="h-7 text-xs px-2"
                                onClick={() => createOneLead(idx)}
                                disabled={l.creating || creatingAllLeads || !l.regionId}
                                data-testid={`button-add-lead-${idx}`}
                              >
                                {l.creating ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add"}
                              </Button>
                            </div>
                          </div>
                        )}

                        {l.created && (
                          <span className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-auto">
                            <CheckCircle2 className="h-3 w-3" /> Added
                          </span>
                        )}

                        {l.skipped && (
                          <span className="text-xs text-muted-foreground mt-auto">Skipped</span>
                        )}

                        {l.error && (
                          <span className="text-xs text-red-500 mt-auto text-right">{l.error}</span>
                        )}
                      </div>
                    </div>

                    {/* Email viewer — collapsible list of related email subjects */}
                    {l.relatedEmails.length > 0 && (
                      <div className="ml-7 border-t pt-1.5">
                        <button
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => updateLead(idx, "emailsExpanded", !l.emailsExpanded)}
                          data-testid={`button-toggle-emails-${idx}`}
                        >
                          {l.emailsExpanded
                            ? <ChevronUp className="h-3 w-3" />
                            : <ChevronDown className="h-3 w-3" />}
                          {l.emailsExpanded ? "Hide" : "View"} {l.relatedEmails.length} email{l.relatedEmails.length !== 1 ? "s" : ""}
                        </button>
                        {l.emailsExpanded && (
                          <ul className="mt-1.5 space-y-0.5">
                            {l.relatedEmails.map((e) => (
                              <li key={e.emailId} className="text-[11px] text-muted-foreground flex items-baseline gap-2">
                                <span className="shrink-0 text-muted-foreground/60">
                                  {e.emailDate ? new Date(e.emailDate).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                                </span>
                                <span className="truncate">{e.subject || "(no subject)"}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Sticky footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-3 flex items-center justify-between gap-4 z-10">
          <div className="text-sm text-muted-foreground flex gap-4">
            {createdBookingCount > 0 && (
              <span className="text-emerald-600 font-medium">{createdBookingCount} bookings created</span>
            )}
            {createdLeadCount > 0 && (
              <span className="text-indigo-600 font-medium">{createdLeadCount} leads added</span>
            )}
            {selectedBookingCount > 0 && <span>{selectedBookingCount} bookings to create</span>}
            {selectedLeadCount > 0 && <span>{selectedLeadCount} leads to add</span>}
          </div>
          <div className="flex gap-2">
            {(createdBookingCount > 0 || createdLeadCount > 0) && (
              <Button variant="outline" size="sm" onClick={() => setStep("done")}>
                View Summary
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleCreateAll}
              disabled={creatingAll || selectedBookingCount === 0}
              data-testid="button-create-all-footer"
            >
              {creatingAll ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</>
              ) : (
                <>Create {selectedBookingCount} Booking{selectedBookingCount !== 1 ? "s" : ""}</>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step: DONE ──────────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="h-10 w-10 text-emerald-500" />
        <div>
          <h1 className="text-2xl font-bold">Recovery Complete</h1>
          <p className="text-muted-foreground text-sm">Records have been restored from email history.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-emerald-600">{createdBookingCount}</div>
            <div className="text-sm text-muted-foreground mt-1 flex items-center justify-center gap-1">
              <Users className="h-4 w-4" /> Clients created
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-indigo-600">{createdLeadCount}</div>
            <div className="text-sm text-muted-foreground mt-1 flex items-center justify-center gap-1">
              <UserCheck className="h-4 w-4" /> Leads restored
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-emerald-600">{restoredCount}</div>
            <div className="text-sm text-muted-foreground mt-1 flex items-center justify-center gap-1">
              <Link2 className="h-4 w-4" /> Invoice links restored
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-amber-500">{newLinkCount}</div>
            <div className="text-sm text-muted-foreground mt-1">New invoice links</div>
          </CardContent>
        </Card>
      </div>

      {newLinkCount > 0 && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4 text-sm text-amber-800 dark:text-amber-300">
          <strong>{newLinkCount} booking{newLinkCount !== 1 ? "s" : ""}</strong> couldn't find the original invoice URL in their email — they received new payment links. Re-send their confirmation emails so they get the updated link.
        </div>
      )}

      {restoredCount > 0 && (
        <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-4 text-sm text-emerald-800 dark:text-emerald-300">
          <strong>{restoredCount} invoice link{restoredCount !== 1 ? "s" : ""}</strong> restored with the original tokens — clients can pay using the same link they already received.
        </div>
      )}

      {createdLeadCount > 0 && (
        <div className="rounded-md bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 p-4 text-sm text-indigo-800 dark:text-indigo-300">
          <strong>{createdLeadCount} lead{createdLeadCount !== 1 ? "s" : ""}</strong> restored with status "contacted" — pick up follow-ups from the Leads section.
        </div>
      )}

      {/* ── Detailed breakdown ─────────────────────────────────────────── */}
      {createdBookingCount > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold text-sm">Created Bookings</h3>
          <div className="rounded-md border divide-y text-sm">
            {bookings.filter((b) => b.created).map((b) => (
              <div key={b.emailId} className="flex items-center justify-between p-2.5 gap-3">
                <div className="min-w-0">
                  <span className="font-medium">{b.accountName}</span>
                  {b.dateOfEvent && (
                    <span className="text-muted-foreground ml-2 text-xs">
                      {new Date(b.dateOfEvent + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  )}
                  {b.invoiceNumber && (
                    <span className="text-muted-foreground ml-2 text-xs">#{b.invoiceNumber}</span>
                  )}
                </div>
                <div className="shrink-0">
                  {b.tokenRestored ? (
                    <Badge className="text-xs bg-emerald-600 text-white">
                      <Link2 className="h-3 w-3 mr-1" /> Link restored
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">
                      <Link2Off className="h-3 w-3 mr-1" /> New link
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {createdLeadCount > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold text-sm">Restored Leads</h3>
          <div className="rounded-md border divide-y text-sm">
            {leads.filter((l) => l.created).map((l) => (
              <div key={l.emailId} className="flex items-center justify-between p-2.5 gap-3">
                <div className="min-w-0">
                  <span className="font-medium">{l.accountName}</span>
                  {l.city && (
                    <span className="text-muted-foreground ml-2 text-xs">
                      {[l.city, l.state].filter(Boolean).join(", ")}
                    </span>
                  )}
                </div>
                <div className="shrink-0">
                  <Badge variant="outline" className="text-xs text-indigo-600 border-indigo-400">
                    {l.relatedEmails.length} email{l.relatedEmails.length !== 1 ? "s" : ""}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Button onClick={() => navigate("/clients")} className="flex-1" data-testid="button-go-to-clients">
          <Users className="h-4 w-4 mr-2" /> Go to Clients
        </Button>
        <Button onClick={() => navigate("/invoices")} variant="outline" className="flex-1" data-testid="button-go-to-invoices">
          <FileText className="h-4 w-4 mr-2" /> Go to Invoices
        </Button>
      </div>

      <Button
        variant="ghost"
        className="w-full text-sm"
        onClick={() => {
          setStep("setup");
          setBookings([]);
          setLeads([]);
          setScannedCount(0);
        }}
      >
        Run another scan
      </Button>
    </div>
  );
}
